
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type DeferredLightShaderUniforms = {
	uAlbedoMap: WebGLUniformLocation,
	uNormalMap: WebGLUniformLocation,
	uDepthMap: WebGLUniformLocation,
	uInvViewProjection: WebGLUniformLocation,
	uCameraPosition: WebGLUniformLocation,
	uLightDataMap: WebGLUniformLocation,
	uLightCount: WebGLUniformLocation,
	uLightDataHeight: WebGLUniformLocation,
	uTileDataMap: WebGLUniformLocation,
	uTilesX: WebGLUniformLocation,
	uTileSize: WebGLUniformLocation,
	uShadowCascadeMap0: WebGLUniformLocation,
	uShadowCascadeMap1: WebGLUniformLocation,
	uShadowCascadeMap2: WebGLUniformLocation,
	uShadowCascadeMap3: WebGLUniformLocation,
	uCascadeViewMatrix: WebGLUniformLocation[],
	uCascadeSplits: WebGLUniformLocation,
	uViewMatrix: WebGLUniformLocation,
	uLightPosition: WebGLUniformLocation,
	uLightColor: WebGLUniformLocation,
	uLightDirection: WebGLUniformLocation,
	uLightRanges: WebGLUniformLocation,
	uLightType: WebGLUniformLocation,
	uAmbientColor: WebGLUniformLocation,
	uSsaoMap: WebGLUniformLocation,
	uShadowMap: WebGLUniformLocation,
	uShadowViewMatrix: WebGLUniformLocation,
	uShadowMapCube: WebGLUniformLocation,
	uShadowLightRange: WebGLUniformLocation,
	uTaaFrameIndex: WebGLUniformLocation
};

export type DeferredLightShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type DeferredLightShaderSettings = ('BATCHED_LIGHTS' | 'TILE_SKIP_SHADOWED' | 'SHADOW_CSM' | 'AMBIENT_PASS' | 'SHADOW_2D' | 'SHADOW_CUBE' | 'DIRECTIONAL')[];

export class DeferredLightShader extends ShaderBase<DeferredLightShaderUniforms, DeferredLightShaderAttributes> {

    private static fragmentShaderSource = `
in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uAlbedoMap;
uniform sampler2D uNormalMap;
uniform sampler2D uDepthMap;
uniform mat4 uInvViewProjection;
uniform vec3 uCameraPosition;

// --- begin: pbr_lighting.glsl ---
// Cook-Torrance PBR BRDF
// D: GGX/Trowbridge-Reitz normal distribution
// F: Schlick fresnel approximation
// G: Smith geometry (Schlick-GGX)

float distribution_ggx(float NdotH, float roughness) {
    float a = roughness * roughness;
    float a2 = a * a;
    float denom = NdotH * NdotH * (a2 - 1.0) + 1.0;
    return a2 / (3.14159265 * denom * denom);
}

float geometry_schlick_ggx(float NdotV, float roughness) {
    float r = roughness + 1.0;
    float k = (r * r) / 8.0;
    return NdotV / (NdotV * (1.0 - k) + k);
}

float geometry_smith(float NdotV, float NdotL, float roughness) {
    return geometry_schlick_ggx(NdotV, roughness) * geometry_schlick_ggx(NdotL, roughness);
}

vec3 fresnel_schlick(float cosTheta, vec3 F0) {
    return F0 + (1.0 - F0) * pow(clamp(1.0 - cosTheta, 0.0, 1.0), 5.0);
}

vec3 pbr_direct_lighting_ndotl(vec3 normal, vec3 surfaceToLight, vec3 surfaceToCamera, vec3 albedo, float metallic, float roughness, vec3 lightColor, float NdotL) {
    vec3 H = normalize(surfaceToLight + surfaceToCamera);

    float NdotV = max(dot(normal, surfaceToCamera), 0.001);
    float NdotH = max(dot(normal, H), 0.0);
    float HdotV = max(dot(H, surfaceToCamera), 0.0);

    // Dielectrics reflect ~4% at normal incidence; metals use their albedo as F0
    vec3 F0 = mix(vec3(0.04), albedo, metallic);

    float D = distribution_ggx(NdotH, roughness);
    float G = geometry_smith(NdotV, NdotL, roughness);
    vec3 F = fresnel_schlick(HdotV, F0);

    // Specular: Cook-Torrance
    vec3 specular = (D * G * F) / max(4.0 * NdotV * NdotL, 0.001);

    // Diffuse: energy-conserving Lambert (metals have no diffuse)
    // Energy conservation: metals have no diffuse, dielectrics scale by (1 - F).
    // PI divisor intentionally omitted — standard game engine simplification to
    // avoid dimming the scene and re-tuning all light intensities.
    vec3 kD = (vec3(1.0) - F) * (1.0 - metallic);
    vec3 diffuse = kD * albedo;

    return (diffuse + specular) * lightColor * NdotL;
}

vec3 pbr_direct_lighting(vec3 normal, vec3 surfaceToLight, vec3 surfaceToCamera, vec3 albedo, float metallic, float roughness, vec3 lightColor) {
    float NdotL = max(dot(normal, surfaceToLight), 0.0);
    return pbr_direct_lighting_ndotl(normal, surfaceToLight, surfaceToCamera, albedo, metallic, roughness, lightColor, NdotL);
}

// --- end: pbr_lighting.glsl ---
// --- begin: world_pos_from_depth.glsl ---
// Reconstruct world / view position from a 24-bit depth sample, screen-space
// UV, and an inverse projection matrix. Replaces an explicit RGBA32F position
// attachment in the G-buffer (~16 bytes/pixel saved).
//
// Reversed-Z: the depth buffer maps near → 1.0 and far → 0.0. Cleared depth
// (= 0.0) means "no geometry was drawn" — callers should discard or treat this
// as background BEFORE calling these functions. The reconstruction itself
// still works at depth == 0.0 (it returns the far-plane point), but the
// result is rarely useful for lighting/AO/decals.
//
// The math is reversal-agnostic: invViewProjection is the inverse of whichever
// projection the depth buffer was rendered with, and the perspective divide
// recovers the correct world position regardless of which side of the NDC z
// range each plane maps to.

// Inputs:
//   depth              — raw [0,1] value from texture(uDepthMap, uv).r
//   uv                 — screen-space UV (vTexCoord, in [0,1])
//   invViewProjection  — inverse of (projection * view) for the current camera
vec3 worldPosFromDepth(float depth, vec2 uv, mat4 invViewProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 world = invViewProjection * ndc;
	return world.xyz / world.w;
}

// View-space variant — pass invProjection (inverse of camera.projectionMatrix)
// when you only need view space. Skips the worldPos → viewPos round-trip,
// which matters for SSAO that samples 16+ positions per fragment.
vec3 viewPosFromDepth(float depth, vec2 uv, mat4 invProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = invProjection * ndc;
	return view.xyz / view.w;
}

// --- end: world_pos_from_depth.glsl ---

#ifdef NOODLE_BATCHED_LIGHTS

#define NOODLE_TILE_SKIP_SHADOWED
// --- begin: tile_light_loop.glsl ---
// Tile-binned light loop shared between the forward (transparent / liquid)
// pipeline and the deferred batched-lighting pass. Returns ONLY the direct
// PBR contribution from tile-assigned lights; ambient is the caller's job.
//
// Compile-time variants:
//
//   NOODLE_TILE_SKIP_SHADOWED — set by the deferred batched-lighting pass.
//     Lights whose d0.w >= 4.0 (shadow flag set) are skipped before fetching
//     d1 — they're already handled by the per-light shadow passes elsewhere
//     in the deferred pipeline. Directional lights are always shadow-flagged
//     in this engine, so the directional branch is compiled out as well.
//
//   (default) — forward path. The shadow flag is stripped via mod() so
//     shadow-flagged lights still contribute. Directional lights take a
//     dedicated branch (no distance cull, CSM applied when NOODLE_SHADOW_CSM
//     is defined). Shadow point/spot lights run unshadowed in forward — the
//     forward pipeline has no per-light shadow pass.
//
// Light data layout (4 RGBA texels per light) matches Light.packGpuData:
//   d0 = position.xyz, type     (type += 4.0 when the light casts shadows)
//   d1 = color.rgb,    outerRange²
//   d2 = direction.xyz, _       (directional / spot only)
//   d3 = innerRange, outerRange, cos(innerCone), cos(outerCone)
//
// Caller is responsible for including pbr_lighting.glsl before this file —
// the deferred shader's per-light pass also needs it, so it stays at the
// caller scope to avoid double-inclusion.

uniform sampler2D uLightDataMap;
uniform int uLightCount;
uniform float uLightDataHeight;
uniform sampler2D uTileDataMap;
uniform int uTilesX;
uniform int uTileSize;

// Hard cap per tile — must match MAX_LIGHTS_PER_TILE in TileLightAssigner.ts.
// The tile data texture row stride is (1 + this), so changing it on one side
// without the other shifts the texelFetch indices and corrupts every lookup.
#define NOODLE_MAX_LIGHTS_PER_TILE 128

#if !defined(NOODLE_TILE_SKIP_SHADOWED) && defined(NOODLE_SHADOW_CSM)
// --- begin: csm_shadow.glsl ---
uniform sampler2D uShadowCascadeMap0;
uniform sampler2D uShadowCascadeMap1;
uniform sampler2D uShadowCascadeMap2;
uniform sampler2D uShadowCascadeMap3;
uniform mat4 uCascadeViewMatrix[4];
uniform vec4 uCascadeSplits;
uniform mat4 uViewMatrix;

// Positive-warp EVSM constant. Must match the value baked into the moments
// resolve shader (\`csm-moments-resolve-fragment.glsl\`) and the temporal
// blend shader (\`csm-moments-blend-fragment.glsl\`). 5.0 is the canonical
// value for fp32 moments — keeps the exponential within precision limits
// while damping the per-blur-kernel variance signal that c=7 was inflating.
// Higher c tightens Chebyshev at silhouettes but amplifies blur-kernel
// variance on glancing surfaces, producing visible per-shadow-texel
// quantization (square cells of differing shadow value) on near-vertical
// walls viewed at low sun. c=5 trades that off for slightly more bleed
// at shadow-on-shadow contacts.
//
// Future high-quality option: full EVSM with both positive and negative
// warps (RGBA32F, 2× memory, ~50% more resolve/blur/blend cost). Stores
// \`(M1+, M2+, M1-, M2-) = (exp(c·d), exp(2c·d), -exp(-c·d), exp(-2c·d))\`
// and takes \`min(chebyshev+, chebyshev-)\` — the negative bound is tight
// where the positive is loose and vice versa, almost completely
// eliminating shadow-on-shadow halos. Skipped for now to keep 60fps on
// mid-tier hardware; gate behind a high/ultra graphics preset if added.
#define C_EVSM 5.0

// Chebyshev variance floor — caps \`1/variance\` blow-up on near-constant
// regions. Tuned in the warped domain; the value is in "exp(c·d) units"
// squared so it scales with C_EVSM. 1e-4 is the canonical Lauritzen value
// scaled into the warped range.
#define MIN_VARIANCE 0.0001

// Light-bleed reduction crush. linstep pushes the lower tail of the
// Chebyshev bound to zero so faint glows under arches / behind silhouettes
// read as fully shadowed instead of "lit at p≈0.15". Tradeoff: also crushes
// genuine penumbra — 0.2 is a moderate setting; bump to 0.3 if Sponza shows
// visible bleed under arches.
#define LIGHT_BLEED_THRESHOLD 0.3

// Slope-scaled receiver bias. On surfaces grazing the light (NdotL near 0)
// the moments blur kernel mixes shadow-map texels with widely different
// recorded depths — \`M2 - M1²\` becomes large enough that Chebyshev returns
// per-texel intermediate p values, then \`linstep(LIGHT_BLEED_THRESHOLD, 1.0, p)\`
// crushes those into visible cell boundaries on the receiver wall. This
// pushes the warped receiver depth toward the light proportionally to
// \`(1 - NdotL)\`, so grazing receivers fall clearly below the moment mean
// (Chebyshev's early-out → 1.0) instead of grazing the variance threshold.
//
// Value is in NDC-z units; with cascade 0's ~115m Z-range, 0.01 ≈ ~115cm
// world detach at the most-grazing case. Surfaces facing the sun directly
// (NdotL=1) get no bias at all, so player shadows on flat ground at high
// sun stay attached. The artifact is most visible at low sun where many
// surfaces are grazing — those same surfaces benefit most from the bias.
// Smaller values (0.002-0.004) leave residual cell artifacts on extremely
// grazing walls; 0.01 is large enough to clear the moments-kernel variance
// range across all cascades while keeping the most-grazing detach under 1m
// (acceptable since players don't notice missing shadows on near-vertical
// surfaces — only on flat ground where bias is zero).
//
// Per-cascade scaling note: a previous attempt scaled this by
// \`cascade0_ZRange / cascade_i_ZRange\` to normalize world-space detach
// across cascades. That dropped cascades 1-3 below the moments-variance
// floor and reintroduced EVSM blob artifacts on distant terrain. The
// variance floor is set by \`texel_size_world / cascade_Z_range\`, which is
// roughly constant across cascades — so the same NDC bias is needed
// regardless of Z-range. Peter-Panning at cascade 3 distance is a
// separate problem that can't be fixed by reducing the bias.
#define SLOPE_BIAS_SCALE 0.01

float linstep(float lo, float hi, float v) {
	return clamp((v - lo) / (hi - lo), 0.0, 1.0);
}

float chebyshev(vec2 moments, float warpedReceiver) {
	if (warpedReceiver <= moments.x) return 1.0;
	float variance = max(moments.y - moments.x * moments.x, MIN_VARIANCE);
	float diff = warpedReceiver - moments.x;
	float pMax = variance / (variance + diff * diff);
	return linstep(LIGHT_BLEED_THRESHOLD, 1.0, pMax);
}

float sample_cascade(sampler2D cascadeMap, vec3 worldPosition, mat4 cascadeMatrix, float slopeBias) {
	vec4 shadowCoord = cascadeMatrix * vec4(worldPosition, 1.0);
	vec3 projectedCoord = shadowCoord.xyz / shadowCoord.w;
	projectedCoord = projectedCoord * 0.5 + 0.5;

	if (projectedCoord.x < 0.0 || projectedCoord.x > 1.0 ||
		projectedCoord.y < 0.0 || projectedCoord.y > 1.0) {
		return 1.0;
	}

	// Hardware bilinear lookup on the pre-blurred moments texture — replaces
	// the 4-tap Poisson PCF kernel. Blurring in moments space (separable
	// Gaussian, applied after resolve) preserves Chebyshev's bound, so the
	// soft-shadow look comes for free without runtime kernel taps.
	vec2 moments = texture(cascadeMap, projectedCoord.xy).rg;
	float warpedReceiver = exp(C_EVSM * (projectedCoord.z - slopeBias));
	return chebyshev(moments, warpedReceiver);
}

float compute_shadow_csm(vec3 worldPosition, float NdotL) {
	float slopeBias = SLOPE_BIAS_SCALE * (1.0 - clamp(NdotL, 0.0, 1.0));
	float viewDepth = (uViewMatrix * vec4(worldPosition, 1.0)).z;

	// Long-shadow overlay: cascade 3 covers mountain-scale casters that the
	// near cascades' tight ortho frustums clip out. Without this, a receiver
	// in cascade 0/1's range under a distant mountain reads "no caster
	// recorded → lit" and the mountain shadow simply disappears as you walk
	// closer. min() against the local cascade keeps near-shadow precision
	// intact while picking up the long-range occluder. Cascade 3 returns 1.0
	// when the receiver projects outside its UV, so receivers far beyond the
	// far cascade fall back to the local result automatically.
	//
	// Cascade 3's per-texel ringing on grazing terrain that contaminated
	// every receiver before is now handled by the slope-scaled receiver
	// bias above (\`slopeBias\`); without that fix, sampling cascade 3
	// unconditionally would re-introduce the speckle.
	float longShadow = sample_cascade(uShadowCascadeMap3, worldPosition, uCascadeViewMatrix[3], slopeBias);

	// Determine cascade and blend between adjacent cascades near boundaries.
	// Inner blend zones use a fixed 5m to keep close-cascade transitions tight;
	// the 2→3 transition uses a wider zone because cascade 3 covers much larger
	// world distances (~2km) so a 5m blend would be invisible at that scale.
	float blendZone = 5.0;

	if (viewDepth > -uCascadeSplits.x) {
		float shadow0 = sample_cascade(uShadowCascadeMap0, worldPosition, uCascadeViewMatrix[0], slopeBias);
		// Blend near the boundary with cascade 1
		float distToBoundary = -viewDepth - (uCascadeSplits.x - blendZone);
		if (distToBoundary > 0.0) {
			float shadow1 = sample_cascade(uShadowCascadeMap1, worldPosition, uCascadeViewMatrix[1], slopeBias);
			return min(longShadow, mix(shadow0, shadow1, smoothstep(0.0, 1.0, distToBoundary / blendZone)));
		}
		return min(longShadow, shadow0);
	} else if (viewDepth > -uCascadeSplits.y) {
		float shadow1 = sample_cascade(uShadowCascadeMap1, worldPosition, uCascadeViewMatrix[1], slopeBias);
		// Blend near the boundary with cascade 2
		float distToBoundary = -viewDepth - (uCascadeSplits.y - blendZone);
		if (distToBoundary > 0.0) {
			float shadow2 = sample_cascade(uShadowCascadeMap2, worldPosition, uCascadeViewMatrix[2], slopeBias);
			return min(longShadow, mix(shadow1, shadow2, smoothstep(0.0, 1.0, distToBoundary / blendZone)));
		}
		return min(longShadow, shadow1);
	} else if (viewDepth > -uCascadeSplits.z) {
		float shadow2 = sample_cascade(uShadowCascadeMap2, worldPosition, uCascadeViewMatrix[2], slopeBias);
		// Blend near the boundary with cascade 3 (mountain-scale).
		float c23BlendZone = 25.0;
		float distToBoundary = -viewDepth - (uCascadeSplits.z - c23BlendZone);
		if (distToBoundary > 0.0) {
			return min(longShadow, mix(shadow2, longShadow, smoothstep(0.0, 1.0, distToBoundary / c23BlendZone)));
		}
		return min(longShadow, shadow2);
	} else if (viewDepth > -uCascadeSplits.w) {
		// Fade shadow out toward the cascade 3 far boundary so the transition to
		// the unshadowed beyond-CSM region is gradual rather than a hard line.
		// Wide fade zone matches the far-cascade scale.
		float fadeZone = 200.0;
		float distToFar = -viewDepth - (uCascadeSplits.w - fadeZone);
		if (distToFar > 0.0) {
			return mix(longShadow, 1.0, smoothstep(0.0, 1.0, distToFar / fadeZone));
		}
		return longShadow;
	}

	return 1.0; // beyond last cascade
}

// --- end: csm_shadow.glsl ---
#endif

vec3 compute_tile_lighting(vec3 position, vec3 normal, vec3 surfaceToCamera, float metallic, float roughness, vec3 albedo) {
	vec3 lighting = vec3(0.0);

	ivec2 tileCoord = ivec2(gl_FragCoord.xy) / uTileSize;
	int tileIndex = tileCoord.y * uTilesX + tileCoord.x;
	int tileCount = min(int(texelFetch(uTileDataMap, ivec2(0, tileIndex), 0).r), NOODLE_MAX_LIGHTS_PER_TILE);

	for (int t = 0; t < tileCount; t++) {
		int i = int(texelFetch(uTileDataMap, ivec2(t + 1, tileIndex), 0).r);
		vec4 d0 = texelFetch(uLightDataMap, ivec2(0, i), 0);

#ifdef NOODLE_TILE_SKIP_SHADOWED
		// Shadow-casting lights are handled by the per-light shadow passes;
		// skip before d1 fetch to save a texelFetch per culled light.
		if (d0.w >= 4.0) continue;
		float lightType = d0.w;
#else
		// Strip the shadow flag so shadow-flagged lights still contribute.
		// Only the directional CSM branch below applies an actual shadow
		// factor — point/spot shadow lights run unshadowed in forward.
		float lightType = mod(d0.w, 4.0);
#endif

		vec4 d1 = texelFetch(uLightDataMap, ivec2(1, i), 0);

		vec3 lightPos = d0.xyz;
		vec3 delta = lightPos - position;
		float dist2 = dot(delta, delta);
		float outerRange2 = d1.w;
#ifdef NOODLE_TILE_SKIP_SHADOWED
		if (dist2 > outerRange2) continue;
#else
		// Directional has effectively infinite range — keep it.
		if (lightType != 1.0 && dist2 > outerRange2) continue;
#endif

		vec3 lightColor = d1.rgb;
		vec3 surfaceToLight;
		float attenuation = 1.0;
		float shadow = 1.0;
		float NdotL;

#ifndef NOODLE_TILE_SKIP_SHADOWED
		if (lightType == 1.0) {
			vec4 d2 = texelFetch(uLightDataMap, ivec2(2, i), 0);
			surfaceToLight = -normalize(d2.xyz);
			NdotL = dot(normal, surfaceToLight);
			if (NdotL <= 0.0) continue;
#ifdef NOODLE_SHADOW_CSM
			shadow = compute_shadow_csm(position, NdotL);
#endif
		} else
#endif
		{
			surfaceToLight = normalize(delta);
			NdotL = dot(normal, surfaceToLight);
			if (NdotL <= 0.0) continue;

			float dist = sqrt(dist2);
			vec4 d3 = texelFetch(uLightDataMap, ivec2(3, i), 0);
			float innerRange = d3.x;
			float outerRange = d3.y;
			attenuation = smoothstep(outerRange, innerRange, dist);

			if (lightType == 2.0) {
				vec4 d2 = texelFetch(uLightDataMap, ivec2(2, i), 0);
				// surfaceToLight = normalize(lightPos - position), so the
				// surface-from-light direction is -surfaceToLight — already
				// normalized and in scope, no need to renormalize delta.
				float dotFromDir = dot(-surfaceToLight, normalize(d2.xyz));
				attenuation = min(attenuation, smoothstep(d3.w, d3.z, dotFromDir));
			}
		}

		lighting += shadow * attenuation * pbr_direct_lighting_ndotl(normal, surfaceToLight, surfaceToCamera, albedo, metallic, roughness, lightColor, NdotL);
	}

	return lighting;
}

// --- end: tile_light_loop.glsl ---

void main(void) {
	float depth = texture(uDepthMap, vTexCoord).r;
	// Geometry marker: reversed-Z cleared depth = 0.0 means "no geometry was drawn".
	if (depth <= 0.0) discard;

	vec4 albedoSample = texture(uAlbedoMap, vTexCoord);
	vec3 albedo = albedoSample.rgb;
	vec4 normalSample = texture(uNormalMap, vTexCoord);
	vec3 normal = normalSample.rgb;
	vec3 position = worldPosFromDepth(depth, vTexCoord, uInvViewProjection);
	vec3 surfaceToCamera = normalize(uCameraPosition - position);

	// PBR parameters packed in g-buffer .a channels (see gbuffer-fragment.glsl)
	float roughness = abs(normalSample.a); // terrain uses negative sentinel; abs() is safe for all geometry
	float metallic = albedoSample.a;

	// Ambient is contributed by the separate NOODLE_AMBIENT_PASS path; the
	// batched pass only accumulates direct light from shadowless point/spot
	// lights. Shadow-casting lights are handled by per-light shadow passes.
	vec3 totalLight = compute_tile_lighting(position, normal, surfaceToCamera, metallic, roughness, albedo);
	fragColor = vec4(totalLight, 1.0);
}

#else

uniform vec3 uLightPosition;
uniform vec3 uLightColor;
uniform vec3 uLightDirection;
uniform vec4 uLightRanges;
uniform float uLightType;

#ifdef NOODLE_AMBIENT_PASS
uniform vec3 uAmbientColor;
uniform sampler2D uSsaoMap;
#endif

#ifdef NOODLE_SHADOW_2D
uniform sampler2D uShadowMap;
uniform mat4 uShadowViewMatrix;
#endif

#ifdef NOODLE_SHADOW_CUBE
uniform samplerCube uShadowMapCube;
uniform float uShadowLightRange;
#endif

#if defined(NOODLE_SHADOW_2D) || defined(NOODLE_SHADOW_CUBE)

// --- begin: shadow_sampling.glsl ---
// Shared PCF shadow sampling utilities.
// Included by csm_shadow.glsl and deferred-light-fragment.glsl.

#ifndef SHADOW_SAMPLING_INCLUDED
#define SHADOW_SAMPLING_INCLUDED

// --- begin: temporal_jitter.glsl ---
// Shared per-frame rotation helper for variance-fed temporal effects (PCF
// shadow taps, SSAO hemisphere orientation, etc.). The same rotation is used
// in multiple places so they stay coherent — see project_temporal_pcf_shadows
// memory for the underlying pattern.

#ifndef TEMPORAL_JITTER_INCLUDED
#define TEMPORAL_JITTER_INCLUDED

// Per-frame rotation index, sourced from Camera.taaFrameIndex. 0 means "TAA
// off, no temporal jitter" — the helper returns identity so consumers fall
// back bit-identically to their pre-temporal sampling pattern. Nonzero
// advances the rotation; combined with the per-pixel hash below this gives
// every pixel a different rotation every frame, and TAA's reprojected-history
// blend averages the resulting angular variance into supersampled output.
uniform int uTaaFrameIndex;

// Build a 2D rotation matrix mixing per-frame and per-pixel terms. Per-pixel
// IGN dominates (×16 weight) so neighbouring pixels see distinct angles
// within a single frame — that keeps TAA's 3×3 neighborhood AABB wide enough
// to not clamp the temporal change. Per-frame term decorrelates successive
// frames so TAA averages distinct sample patterns. Returns identity when the
// index is zero, which is the only path taken when TAA is off.
mat2 temporal_jitter_matrix() {
	if (uTaaFrameIndex == 0) {
		return mat2(1.0, 0.0, 0.0, 1.0);
	}
	// Interleaved gradient noise (Jiminez 2014) — cheap blue-noise-ish hash.
	float ign = fract(52.9829189 * fract(0.06711056 * gl_FragCoord.x + 0.00583715 * gl_FragCoord.y));
	float a = (float(uTaaFrameIndex) + ign * 16.0) * 2.39996323;
	float c = cos(a);
	float s = sin(a);
	return mat2(c, s, -s, c);
}

#endif // TEMPORAL_JITTER_INCLUDED

// --- end: temporal_jitter.glsl ---

// Receiver-plane depth bias: per-pixel slope of the receiver's projected depth
// in shadow-UV space. Returns \`(dz/du, dz/dv)\` so a PCF tap at UV offset \`o\`
// can compare against \`projectedCoord.z + dot(biasUV, o)\` — the receiver
// plane extrapolated to the tap location. Smooth across triangle boundaries
// (receiver pixels share the surface plane, not per-caster-triangle), so this
// kills the acne that polygon-offset slope-scale creates as it pushes each
// caster triangle back by a different amount.
//
// Clamp at ±1.0 caps the per-tap NDC offset to \`1.0 / spread\` (≈7e-4 NDC at
// the standard CSM spread of 1400) — about 8cm in cascade 0, 2.5m in cascade
// 3 worst case at silhouettes. Beyond ±1.0 means a slope where receiver depth
// covers the full Z-range across one UV unit — that's silhouette territory
// where derivatives are bogus anyway (helper threads, cross-triangle).
vec2 receiver_plane_depth_bias(vec3 projectedCoord) {
	vec3 dx = dFdx(projectedCoord);
	vec3 dy = dFdy(projectedCoord);
	float det = dx.x * dy.y - dx.y * dy.x;
	if (abs(det) < 1e-8) return vec2(0.0);
	vec2 biasUV;
	biasUV.x = (dy.y * dx.z - dx.y * dy.z) / det;
	biasUV.y = (dx.x * dy.z - dy.x * dx.z) / det;
	return clamp(biasUV, vec2(-1.0), vec2(1.0));
}

// PCF shadow sampling for 2D shadow maps (directional, spot)
float pcf_shadow_2d(sampler2D shadowMap, vec3 projectedCoord, float bias, float spread) {
	vec2 poissonDisk[4];
	poissonDisk[0] = vec2(-0.94201624, -0.39906216);
	poissonDisk[1] = vec2(0.94558609, -0.76890725);
	poissonDisk[2] = vec2(-0.094184101, -0.92938870);
	poissonDisk[3] = vec2(0.34495938, 0.29387760);

	mat2 R = temporal_jitter_matrix();
	vec2 biasUV = receiver_plane_depth_bias(projectedCoord);

	float visibility = 1.0;
	float refDepth = projectedCoord.z - bias;
	for (int i = 0; i < 4; i++) {
		vec2 offset = (R * poissonDisk[i]) / spread;
		float currentDepth = refDepth + dot(biasUV, offset);
		float depth = texture(shadowMap, projectedCoord.xy + offset).r;
		if (depth < currentDepth) {
			visibility -= 0.25;
		}
	}
	return visibility;
}

// PCF shadow sampling for cube shadow maps (point lights). Rotation acts on
// the xy plane of the offset only — z is left alone so the rotated direction
// still samples a hemisphere around the original lookup vector.
float pcf_shadow_cube(samplerCube shadowMap, vec3 direction, float currentDepth, float spread) {
	vec3 poissonDisk3[4];
	poissonDisk3[0] = vec3(-0.94201624, -0.39906216, 0.5469423);
	poissonDisk3[1] = vec3(0.94558609, -0.76890725, -0.104434);
	poissonDisk3[2] = vec3(-0.094184101, -0.92938870, 0.0823491);
	poissonDisk3[3] = vec3(0.34495938, 0.29387760, -0.2846281);

	mat2 R = temporal_jitter_matrix();

	float visibility = 1.0;
	for (int i = 0; i < 4; i++) {
		vec2 xy = R * poissonDisk3[i].xy;
		vec3 s = direction + vec3(xy, poissonDisk3[i].z) / spread;
		float depth = texture(shadowMap, s).r;
		if (depth < currentDepth) {
			visibility -= 0.25;
		}
	}
	return visibility;
}

#endif // SHADOW_SAMPLING_INCLUDED

// --- end: shadow_sampling.glsl ---

#define SHADOW_BIAS_2D 0.0003
#define SHADOW_BIAS_CUBE 0.1

float compute_shadow_2d(vec3 worldPosition) {
#ifdef NOODLE_SHADOW_2D
	vec4 shadowCoord = uShadowViewMatrix * vec4(worldPosition, 1.0);
	vec3 projectedCoord = shadowCoord.xyz / shadowCoord.w;

	bool inRange =
		shadowCoord.w > 0.0 &&
		projectedCoord.x >= -1.0 &&
		projectedCoord.x <= 1.0 &&
		projectedCoord.y >= -1.0 &&
		projectedCoord.y <= 1.0;

	if (!inRange) return 1.0;

	projectedCoord = projectedCoord * 0.5 + 0.5;
	return pcf_shadow_2d(uShadowMap, projectedCoord, SHADOW_BIAS_2D, 700.0);
#else
	return 1.0;
#endif
}

float compute_shadow_cube(vec3 worldPosition) {
#ifdef NOODLE_SHADOW_CUBE
	vec3 direction = worldPosition - uLightPosition;
	float distanceToLight = length(direction);
	if (distanceToLight > uShadowLightRange) return 1.0;

	float currentDepth = distanceToLight - SHADOW_BIAS_CUBE;
	// Cube spread divides the offset added to a unit-length-ish direction
	// vector. 25.0 keeps the sweep small (~0.04 in direction-space) so the
	// disk samples land in adjacent cubemap texels rather than blurring
	// across faces. Tuned for the default 1024³ point shadow map; stays
	// reasonable across the 512–2048 range that GraphicsSettings exposes.
	return pcf_shadow_cube(uShadowMapCube, direction, currentDepth, 25.0);
#else
	return 1.0;
#endif
}

#endif

#ifdef NOODLE_SHADOW_CSM
// --- begin: csm_shadow.glsl ---
uniform sampler2D uShadowCascadeMap0;
uniform sampler2D uShadowCascadeMap1;
uniform sampler2D uShadowCascadeMap2;
uniform sampler2D uShadowCascadeMap3;
uniform mat4 uCascadeViewMatrix[4];
uniform vec4 uCascadeSplits;
uniform mat4 uViewMatrix;

// Positive-warp EVSM constant. Must match the value baked into the moments
// resolve shader (\`csm-moments-resolve-fragment.glsl\`) and the temporal
// blend shader (\`csm-moments-blend-fragment.glsl\`). 5.0 is the canonical
// value for fp32 moments — keeps the exponential within precision limits
// while damping the per-blur-kernel variance signal that c=7 was inflating.
// Higher c tightens Chebyshev at silhouettes but amplifies blur-kernel
// variance on glancing surfaces, producing visible per-shadow-texel
// quantization (square cells of differing shadow value) on near-vertical
// walls viewed at low sun. c=5 trades that off for slightly more bleed
// at shadow-on-shadow contacts.
//
// Future high-quality option: full EVSM with both positive and negative
// warps (RGBA32F, 2× memory, ~50% more resolve/blur/blend cost). Stores
// \`(M1+, M2+, M1-, M2-) = (exp(c·d), exp(2c·d), -exp(-c·d), exp(-2c·d))\`
// and takes \`min(chebyshev+, chebyshev-)\` — the negative bound is tight
// where the positive is loose and vice versa, almost completely
// eliminating shadow-on-shadow halos. Skipped for now to keep 60fps on
// mid-tier hardware; gate behind a high/ultra graphics preset if added.
#define C_EVSM 5.0

// Chebyshev variance floor — caps \`1/variance\` blow-up on near-constant
// regions. Tuned in the warped domain; the value is in "exp(c·d) units"
// squared so it scales with C_EVSM. 1e-4 is the canonical Lauritzen value
// scaled into the warped range.
#define MIN_VARIANCE 0.0001

// Light-bleed reduction crush. linstep pushes the lower tail of the
// Chebyshev bound to zero so faint glows under arches / behind silhouettes
// read as fully shadowed instead of "lit at p≈0.15". Tradeoff: also crushes
// genuine penumbra — 0.2 is a moderate setting; bump to 0.3 if Sponza shows
// visible bleed under arches.
#define LIGHT_BLEED_THRESHOLD 0.3

// Slope-scaled receiver bias. On surfaces grazing the light (NdotL near 0)
// the moments blur kernel mixes shadow-map texels with widely different
// recorded depths — \`M2 - M1²\` becomes large enough that Chebyshev returns
// per-texel intermediate p values, then \`linstep(LIGHT_BLEED_THRESHOLD, 1.0, p)\`
// crushes those into visible cell boundaries on the receiver wall. This
// pushes the warped receiver depth toward the light proportionally to
// \`(1 - NdotL)\`, so grazing receivers fall clearly below the moment mean
// (Chebyshev's early-out → 1.0) instead of grazing the variance threshold.
//
// Value is in NDC-z units; with cascade 0's ~115m Z-range, 0.01 ≈ ~115cm
// world detach at the most-grazing case. Surfaces facing the sun directly
// (NdotL=1) get no bias at all, so player shadows on flat ground at high
// sun stay attached. The artifact is most visible at low sun where many
// surfaces are grazing — those same surfaces benefit most from the bias.
// Smaller values (0.002-0.004) leave residual cell artifacts on extremely
// grazing walls; 0.01 is large enough to clear the moments-kernel variance
// range across all cascades while keeping the most-grazing detach under 1m
// (acceptable since players don't notice missing shadows on near-vertical
// surfaces — only on flat ground where bias is zero).
//
// Per-cascade scaling note: a previous attempt scaled this by
// \`cascade0_ZRange / cascade_i_ZRange\` to normalize world-space detach
// across cascades. That dropped cascades 1-3 below the moments-variance
// floor and reintroduced EVSM blob artifacts on distant terrain. The
// variance floor is set by \`texel_size_world / cascade_Z_range\`, which is
// roughly constant across cascades — so the same NDC bias is needed
// regardless of Z-range. Peter-Panning at cascade 3 distance is a
// separate problem that can't be fixed by reducing the bias.
#define SLOPE_BIAS_SCALE 0.01

float linstep(float lo, float hi, float v) {
	return clamp((v - lo) / (hi - lo), 0.0, 1.0);
}

float chebyshev(vec2 moments, float warpedReceiver) {
	if (warpedReceiver <= moments.x) return 1.0;
	float variance = max(moments.y - moments.x * moments.x, MIN_VARIANCE);
	float diff = warpedReceiver - moments.x;
	float pMax = variance / (variance + diff * diff);
	return linstep(LIGHT_BLEED_THRESHOLD, 1.0, pMax);
}

float sample_cascade(sampler2D cascadeMap, vec3 worldPosition, mat4 cascadeMatrix, float slopeBias) {
	vec4 shadowCoord = cascadeMatrix * vec4(worldPosition, 1.0);
	vec3 projectedCoord = shadowCoord.xyz / shadowCoord.w;
	projectedCoord = projectedCoord * 0.5 + 0.5;

	if (projectedCoord.x < 0.0 || projectedCoord.x > 1.0 ||
		projectedCoord.y < 0.0 || projectedCoord.y > 1.0) {
		return 1.0;
	}

	// Hardware bilinear lookup on the pre-blurred moments texture — replaces
	// the 4-tap Poisson PCF kernel. Blurring in moments space (separable
	// Gaussian, applied after resolve) preserves Chebyshev's bound, so the
	// soft-shadow look comes for free without runtime kernel taps.
	vec2 moments = texture(cascadeMap, projectedCoord.xy).rg;
	float warpedReceiver = exp(C_EVSM * (projectedCoord.z - slopeBias));
	return chebyshev(moments, warpedReceiver);
}

float compute_shadow_csm(vec3 worldPosition, float NdotL) {
	float slopeBias = SLOPE_BIAS_SCALE * (1.0 - clamp(NdotL, 0.0, 1.0));
	float viewDepth = (uViewMatrix * vec4(worldPosition, 1.0)).z;

	// Long-shadow overlay: cascade 3 covers mountain-scale casters that the
	// near cascades' tight ortho frustums clip out. Without this, a receiver
	// in cascade 0/1's range under a distant mountain reads "no caster
	// recorded → lit" and the mountain shadow simply disappears as you walk
	// closer. min() against the local cascade keeps near-shadow precision
	// intact while picking up the long-range occluder. Cascade 3 returns 1.0
	// when the receiver projects outside its UV, so receivers far beyond the
	// far cascade fall back to the local result automatically.
	//
	// Cascade 3's per-texel ringing on grazing terrain that contaminated
	// every receiver before is now handled by the slope-scaled receiver
	// bias above (\`slopeBias\`); without that fix, sampling cascade 3
	// unconditionally would re-introduce the speckle.
	float longShadow = sample_cascade(uShadowCascadeMap3, worldPosition, uCascadeViewMatrix[3], slopeBias);

	// Determine cascade and blend between adjacent cascades near boundaries.
	// Inner blend zones use a fixed 5m to keep close-cascade transitions tight;
	// the 2→3 transition uses a wider zone because cascade 3 covers much larger
	// world distances (~2km) so a 5m blend would be invisible at that scale.
	float blendZone = 5.0;

	if (viewDepth > -uCascadeSplits.x) {
		float shadow0 = sample_cascade(uShadowCascadeMap0, worldPosition, uCascadeViewMatrix[0], slopeBias);
		// Blend near the boundary with cascade 1
		float distToBoundary = -viewDepth - (uCascadeSplits.x - blendZone);
		if (distToBoundary > 0.0) {
			float shadow1 = sample_cascade(uShadowCascadeMap1, worldPosition, uCascadeViewMatrix[1], slopeBias);
			return min(longShadow, mix(shadow0, shadow1, smoothstep(0.0, 1.0, distToBoundary / blendZone)));
		}
		return min(longShadow, shadow0);
	} else if (viewDepth > -uCascadeSplits.y) {
		float shadow1 = sample_cascade(uShadowCascadeMap1, worldPosition, uCascadeViewMatrix[1], slopeBias);
		// Blend near the boundary with cascade 2
		float distToBoundary = -viewDepth - (uCascadeSplits.y - blendZone);
		if (distToBoundary > 0.0) {
			float shadow2 = sample_cascade(uShadowCascadeMap2, worldPosition, uCascadeViewMatrix[2], slopeBias);
			return min(longShadow, mix(shadow1, shadow2, smoothstep(0.0, 1.0, distToBoundary / blendZone)));
		}
		return min(longShadow, shadow1);
	} else if (viewDepth > -uCascadeSplits.z) {
		float shadow2 = sample_cascade(uShadowCascadeMap2, worldPosition, uCascadeViewMatrix[2], slopeBias);
		// Blend near the boundary with cascade 3 (mountain-scale).
		float c23BlendZone = 25.0;
		float distToBoundary = -viewDepth - (uCascadeSplits.z - c23BlendZone);
		if (distToBoundary > 0.0) {
			return min(longShadow, mix(shadow2, longShadow, smoothstep(0.0, 1.0, distToBoundary / c23BlendZone)));
		}
		return min(longShadow, shadow2);
	} else if (viewDepth > -uCascadeSplits.w) {
		// Fade shadow out toward the cascade 3 far boundary so the transition to
		// the unshadowed beyond-CSM region is gradual rather than a hard line.
		// Wide fade zone matches the far-cascade scale.
		float fadeZone = 200.0;
		float distToFar = -viewDepth - (uCascadeSplits.w - fadeZone);
		if (distToFar > 0.0) {
			return mix(longShadow, 1.0, smoothstep(0.0, 1.0, distToFar / fadeZone));
		}
		return longShadow;
	}

	return 1.0; // beyond last cascade
}

// --- end: csm_shadow.glsl ---
#endif

void main(void) {
	float depth = texture(uDepthMap, vTexCoord).r;
	// Geometry marker: reversed-Z cleared depth = 0.0 means "no geometry was drawn".
	if (depth <= 0.0) discard;

	vec4 albedoSample = texture(uAlbedoMap, vTexCoord);
	vec3 albedo = albedoSample.rgb;
	vec4 normalSample = texture(uNormalMap, vTexCoord);
	vec3 normal = normalSample.rgb;
	vec3 position = worldPosFromDepth(depth, vTexCoord, uInvViewProjection);

	// PBR parameters packed in g-buffer .a channels (see gbuffer-fragment.glsl)
	float roughness = abs(normalSample.a); // terrain uses negative sentinel; abs() is safe for all geometry
	float metallic = albedoSample.a;

#ifdef NOODLE_AMBIENT_PASS
	float ao = texture(uSsaoMap, vTexCoord).r;
	fragColor = vec4(albedo * uAmbientColor * ao, 1.0);
	// Forward depth from the depth buffer so the forward transparency pass can
	// depth-test against deferred geometry. Depth is already in [0,1] window space.
	gl_FragDepth = depth;
	return;
#endif

	vec3 surfaceToCamera = normalize(uCameraPosition - position);
	float attenuation = 1.0;
	vec3 surfaceToLight;

#ifdef NOODLE_DIRECTIONAL
	// Directional light: parallel rays, no distance attenuation
	surfaceToLight = -normalize(uLightDirection);
#else
	// Point/spot light: position-based with distance attenuation
	surfaceToLight = normalize(uLightPosition - position);
	float distanceToLight = length(uLightPosition - position);
	float innerRange = uLightRanges.x;
	float outerRange = uLightRanges.y;
	attenuation = smoothstep(outerRange, innerRange, distanceToLight);

	if (uLightType == 2.0) {
		// Spot light: apply cone falloff
		vec3 lightDir = normalize(uLightDirection);
		float dotFromDirection = dot(normalize(position - uLightPosition), lightDir);
		float dotInnerLimit = uLightRanges.z;
		float dotOuterLimit = uLightRanges.w;
		float spotEffect = smoothstep(dotOuterLimit, dotInnerLimit, dotFromDirection);
		attenuation = min(attenuation, spotEffect);
	}
#endif

	float NdotL = max(0.0, dot(normal, surfaceToLight));

	// Per-light shadow
	float shadow = 1.0;
#ifdef NOODLE_SHADOW_2D
	shadow = compute_shadow_2d(position);
#endif
#ifdef NOODLE_SHADOW_CUBE
	shadow = compute_shadow_cube(position);
#endif
#ifdef NOODLE_SHADOW_CSM
	shadow = compute_shadow_csm(position, NdotL);
#endif

	vec3 result = shadow * attenuation * pbr_direct_lighting(normal, surfaceToLight, surfaceToCamera, albedo, metallic, roughness, uLightColor);
	fragColor = vec4(result, 1.0);
}

#endif
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: DeferredLightShaderUniformsContainer;
    public attributesContainer: DeferredLightShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: DeferredLightShaderSettings) {
        super(ctx, DeferredLightShader.fragmentShaderSource, DeferredLightShader.vertexShaderSource, settings);
        this.uniformsContainer = new DeferredLightShaderUniformsContainer(ctx, this);
        this.attributesContainer = new DeferredLightShaderShaderAttributesContainer(ctx, this);
    }
}

class DeferredLightShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: DeferredLightShader) {
        super(ctx);
    }

    public setAlbedoMap(texture: Texture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uAlbedoMap', this.shader.uniforms.uAlbedoMap, texture, 'sampler2D');
        return this;
    }

    public setNormalMap(texture: Texture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uNormalMap', this.shader.uniforms.uNormalMap, texture, 'sampler2D');
        return this;
    }

    public setDepthMap(texture: Texture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uDepthMap', this.shader.uniforms.uDepthMap, texture, 'sampler2D');
        return this;
    }

    public setInvViewProjection(value: mat4 | number[]) : DeferredLightShaderUniformsContainer {
        this.setValue('uInvViewProjection', this.shader.uniforms.uInvViewProjection, value, 'mat4');
        return this;
    }

    public setCameraPosition(value: vec3 | number[]) : DeferredLightShaderUniformsContainer {
        this.setValue('uCameraPosition', this.shader.uniforms.uCameraPosition, value, 'vec3');
        return this;
    }

    public setLightDataMap(texture: Texture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uLightDataMap', this.shader.uniforms.uLightDataMap, texture, 'sampler2D');
        return this;
    }

    public setLightCount(value: number) : DeferredLightShaderUniformsContainer {
        this.setValue('uLightCount', this.shader.uniforms.uLightCount, value, 'int');
        return this;
    }

    public setLightDataHeight(value: number) : DeferredLightShaderUniformsContainer {
        this.setValue('uLightDataHeight', this.shader.uniforms.uLightDataHeight, value, 'float');
        return this;
    }

    public setTileDataMap(texture: Texture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uTileDataMap', this.shader.uniforms.uTileDataMap, texture, 'sampler2D');
        return this;
    }

    public setTilesX(value: number) : DeferredLightShaderUniformsContainer {
        this.setValue('uTilesX', this.shader.uniforms.uTilesX, value, 'int');
        return this;
    }

    public setTileSize(value: number) : DeferredLightShaderUniformsContainer {
        this.setValue('uTileSize', this.shader.uniforms.uTileSize, value, 'int');
        return this;
    }

    public setShadowCascadeMap0(texture: Texture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap0', this.shader.uniforms.uShadowCascadeMap0, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap1(texture: Texture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap1', this.shader.uniforms.uShadowCascadeMap1, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap2(texture: Texture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap2', this.shader.uniforms.uShadowCascadeMap2, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap3(texture: Texture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap3', this.shader.uniforms.uShadowCascadeMap3, texture, 'sampler2D');
        return this;
    }

    public setCascadeViewMatrix(index: number, value: mat4 | number[]) : DeferredLightShaderUniformsContainer {
        if(index < 0 || index >= 4) throw new Error('Invalid index used for uCascadeViewMatrix: ' + index);
        this.setValue('uCascadeViewMatrix', this.shader.uniforms.uCascadeViewMatrix[index], value, 'mat4');
        return this;
    }

    public setCascadeSplits(value: vec4 | number[]) : DeferredLightShaderUniformsContainer {
        this.setValue('uCascadeSplits', this.shader.uniforms.uCascadeSplits, value, 'vec4');
        return this;
    }

    public setViewMatrix(value: mat4 | number[]) : DeferredLightShaderUniformsContainer {
        this.setValue('uViewMatrix', this.shader.uniforms.uViewMatrix, value, 'mat4');
        return this;
    }

    public setLightPosition(value: vec3 | number[]) : DeferredLightShaderUniformsContainer {
        this.setValue('uLightPosition', this.shader.uniforms.uLightPosition, value, 'vec3');
        return this;
    }

    public setLightColor(value: vec3 | number[]) : DeferredLightShaderUniformsContainer {
        this.setValue('uLightColor', this.shader.uniforms.uLightColor, value, 'vec3');
        return this;
    }

    public setLightDirection(value: vec3 | number[]) : DeferredLightShaderUniformsContainer {
        this.setValue('uLightDirection', this.shader.uniforms.uLightDirection, value, 'vec3');
        return this;
    }

    public setLightRanges(value: vec4 | number[]) : DeferredLightShaderUniformsContainer {
        this.setValue('uLightRanges', this.shader.uniforms.uLightRanges, value, 'vec4');
        return this;
    }

    public setLightType(value: number) : DeferredLightShaderUniformsContainer {
        this.setValue('uLightType', this.shader.uniforms.uLightType, value, 'float');
        return this;
    }

    public setAmbientColor(value: vec3 | number[]) : DeferredLightShaderUniformsContainer {
        this.setValue('uAmbientColor', this.shader.uniforms.uAmbientColor, value, 'vec3');
        return this;
    }

    public setSsaoMap(texture: Texture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uSsaoMap', this.shader.uniforms.uSsaoMap, texture, 'sampler2D');
        return this;
    }

    public setShadowMap(texture: Texture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uShadowMap', this.shader.uniforms.uShadowMap, texture, 'sampler2D');
        return this;
    }

    public setShadowViewMatrix(value: mat4 | number[]) : DeferredLightShaderUniformsContainer {
        this.setValue('uShadowViewMatrix', this.shader.uniforms.uShadowViewMatrix, value, 'mat4');
        return this;
    }

    public setShadowMapCube(texture: CubeMapTexture) : DeferredLightShaderUniformsContainer {
        this.setTextureValue('uShadowMapCube', this.shader.uniforms.uShadowMapCube, texture, 'samplerCube');
        return this;
    }

    public setShadowLightRange(value: number) : DeferredLightShaderUniformsContainer {
        this.setValue('uShadowLightRange', this.shader.uniforms.uShadowLightRange, value, 'float');
        return this;
    }

    public setTaaFrameIndex(value: number) : DeferredLightShaderUniformsContainer {
        this.setValue('uTaaFrameIndex', this.shader.uniforms.uTaaFrameIndex, value, 'int');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uAlbedoMap', uniform: this.shader.uniforms.uAlbedoMap, dataType: 'sampler2D' },
			{ name: 'uNormalMap', uniform: this.shader.uniforms.uNormalMap, dataType: 'sampler2D' },
			{ name: 'uDepthMap', uniform: this.shader.uniforms.uDepthMap, dataType: 'sampler2D' },
			{ name: 'uLightDataMap', uniform: this.shader.uniforms.uLightDataMap, dataType: 'sampler2D' },
			{ name: 'uTileDataMap', uniform: this.shader.uniforms.uTileDataMap, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap0', uniform: this.shader.uniforms.uShadowCascadeMap0, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap1', uniform: this.shader.uniforms.uShadowCascadeMap1, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap2', uniform: this.shader.uniforms.uShadowCascadeMap2, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap3', uniform: this.shader.uniforms.uShadowCascadeMap3, dataType: 'sampler2D' },
			{ name: 'uSsaoMap', uniform: this.shader.uniforms.uSsaoMap, dataType: 'sampler2D' },
			{ name: 'uShadowMap', uniform: this.shader.uniforms.uShadowMap, dataType: 'sampler2D' },
			{ name: 'uShadowMapCube', uniform: this.shader.uniforms.uShadowMapCube, dataType: 'samplerCube' }
        ];
    }
}

class DeferredLightShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: DeferredLightShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : DeferredLightShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : DeferredLightShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}